// import styles from ""./app.module.scss";
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import MenuContext, { initializeMenuEvents, MenuContextOptions } from "@menu/core/menu.context";
import { inboxCache } from "@menu/core/inbox";
import { maxMobileWidth, rootElementId } from "@menu/core/constants";
import useEffectOnce from "@menu/core/hooks/use-effect-once";
import { ConfigService, SettingsService } from "@menu/core/services";
import getSiteAndCulture from "@menu/core/lib/get-site-and-culture";
import { MenuConfig } from "@menu/core/types";
import Spinner from "@menu/components/shared/spinner";
import Menu from "@menu/components/menu";
import determineIfLoggedIn from "@menu/core/lib/determine-if-logged-in";
import getCountryAndCulture from "@menu/core/lib/get-country-and-culture";

declare global {
  interface String { toLowerUnderscoreCase(): string; }
}

require("./core/lib/lower-underscore-case");

const siteDataAttribute = {
  logoUrl: "data-site-logo-url",
  logoHeight: "data-site-logo-height",
  logoMobileHeight: "data-site-logo-mobile-height",
  title: "data-site-title"
};

type MenuAppProps = {
  updateLocation?: boolean,
  location?: any,
  site?: string | undefined
}

const MenuApp = observer((props: MenuAppProps) => {
  const [context, setContext] = useState<MenuContext | undefined>(undefined);
  const { updateLocation = false, location } = props;

  const updateConfigFromAttribute = (config: MenuConfig) => {
    const element = document.getElementById(rootElementId);

    const mainSite = config.sites[0];

    const a = siteDataAttribute;

    if (element?.hasAttribute(a.logoUrl)) {
      mainSite.image = element.getAttribute(a.logoUrl) ?? "";
    }

    if (element?.hasAttribute(a.logoHeight)) {
      mainSite.logoHeight = element.getAttribute(a.logoHeight) ?? "";
    }

    if (element?.hasAttribute(a.logoMobileHeight)) {
      mainSite.logoMobileHeight = element.getAttribute(a.logoMobileHeight) ?? "";
    }

    if (element?.hasAttribute(a.title)) {
      mainSite.title = element.getAttribute(a.title) ?? "";
    }
  }

  const initialize = async () => {
    let { site, culture } = getSiteAndCulture();

    if (!!props?.site) {
      site = props.site;
    }

    const service = new ConfigService();
    const countriesTask = service.getCountries(site);
    const configTask = service.getConfig(culture, site);
    const mainSiteTask = !site ? Promise.resolve(undefined) : service.getConfig(culture, "");

    const countries = await countriesTask;
    const config = await configTask;

    if (!config) {
      console.warn("Menu config is null");
      return;
    }

    updateConfigFromAttribute(config);

    const mainConfig = await mainSiteTask;

    const { country, countryCulture } = getCountryAndCulture(countries, culture);

    const menuContextOptions: MenuContextOptions = {
      config,
      siteName: site,
      countries,
      country,
      culture: countryCulture
    }
    const menuContext = new MenuContext(menuContextOptions);

    const settings = await new SettingsService().getSettings(menuContext.countryCode, window.location.pathname);

    menuContext.updateSettings(settings);

    if (!!mainConfig) {
      menuContext.updateMainSettings(mainConfig);
    }

    if (inboxCache.isCached) {
      menuContext.updateInbox(inboxCache.value);
    }

    const setIsMobile = () => {
      const isMobile = window.innerWidth <= maxMobileWidth;

      if (!menuContext.isMobile && isMobile) {
        menuContext.updateDisplayMobileNav(false);
      }

      menuContext.updateIsMobile(isMobile);
    }

    setIsMobile();

    determineIfLoggedIn(menuContext);

    initializeMenuEvents(menuContext, determineIfLoggedIn);

    setContext(menuContext);

    window.addEventListener("resize", setIsMobile);
  }

  useEffectOnce(() => {
    //setTimeout(() => load(), 1000); //use to simulate getting data from web service
    const load = async () => {
      initialize();
    }

    load();
  });

  if (updateLocation) {
    useEffect(() => {
      context?.updateLocation(location);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);
  }

  return !context
    ? <Spinner />
    : <Menu context={context} />;
});

export default MenuApp;
